import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  AcademicCapIcon, 
  CodeBracketIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import { organizationStructuredData, faqStructuredData } from '../data/seo';

const Support = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    urgency: 'medium',
    description: '',
    attachments: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      attachments: e.target.files[0]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ 
        name: '', 
        email: '', 
        subject: '', 
        urgency: 'medium', 
        description: '', 
        attachments: null 
      });
    }, 2000);
  };

  const supportTypes = [
    {
      icon: CodeBracketIcon,
      title: 'Code Review',
      description: 'Get feedback on your code, debugging help, and optimization suggestions',
      price: '$15/hour',
      duration: '30-60 minutes',
      color: 'bg-green-500'
    },
    {
      icon: DocumentTextIcon,
      title: 'Assignment Help',
      description: 'Assistance with coding assignments, homework, and academic projects',
      price: '$20/hour',
      duration: '60-90 minutes',
      color: 'bg-blue-500'
    },
    {
      icon: AcademicCapIcon,
      title: 'Project Support',
      description: 'Guidance on personal projects, portfolio building, and career development',
      price: '$30/hour',
      duration: '90-120 minutes',
      color: 'bg-purple-500'
    },
    {
      icon: ClockIcon,
      title: 'Emergency Help',
      description: 'Urgent debugging, deadline support, and last-minute project fixes',
      price: '$50/hour',
      duration: '30-60 minutes',
      color: 'bg-red-500'
    }
  ];

  const faqs = [
    {
      question: "What types of assignments can you help with?",
      answer: "I can help with Python, JavaScript, React, data analysis, machine learning projects, and most programming assignments. I focus on teaching concepts rather than just providing answers."
    },
    {
      question: "How quickly can you respond to urgent requests?",
      answer: "For emergency support, I typically respond within 2-4 hours during business hours. For regular requests, I aim to respond within 24 hours."
    },
    {
      question: "Do you provide code solutions or explanations?",
      answer: "I prioritize teaching and explanation over providing direct solutions. I'll guide you through the problem-solving process and help you understand the concepts."
    },
    {
      question: "Can you help with debugging existing code?",
      answer: "Absolutely! I can review your code, identify issues, and help you fix bugs while explaining the root causes and prevention strategies."
    },
    {
      question: "What file formats do you accept for code review?",
      answer: "I can work with any text-based code files (.py, .js, .jsx, .html, .css, etc.) and can also review code through GitHub repositories or shared screens."
    },
    {
      question: "Do you offer ongoing project support?",
      answer: "Yes, I provide ongoing support for long-term projects, helping you plan, implement, and troubleshoot throughout the development process."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO
        title="Programming Assignment Help & Project Support - Get Expert Coding Assistance"
        description="Get personalized help with your coding assignments, projects, and technical challenges. Expert debugging, code review, and learning guidance from Duke. Quick response times and flexible scheduling."
        keywords="programming assignment help, coding project support, code review service, debugging help, programming tutor, coding homework help, technical project assistance"
        canonical="/support"
        ogImage="/og-support.jpg"
        structuredData={[organizationStructuredData, faqStructuredData(faqs)]}
      />
      {/* Header */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Assignment & Project Support
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Get personalized help with your coding assignments, projects, and technical challenges. 
              Learn through guided problem-solving and expert mentorship.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Support Types */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Types of Support
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Choose the support type that best fits your needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {supportTypes.map((support, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className={`${support.color} p-6 text-white rounded-t-xl`}>
                  <div className="flex items-center justify-between mb-4">
                    <support.icon className="h-8 w-8" />
                    <div className="text-right">
                      <div className="text-lg font-bold">{support.price}</div>
                      <div className="text-sm opacity-90">{support.duration}</div>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold">{support.title}</h3>
                </div>
                
                <div className="p-6">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {support.description}
                  </p>
                  <button className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-200">
                    Request Help
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Request Form */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Request Support
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Python assignment help, React project debugging"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Urgency Level
                  </label>
                  <select
                    id="urgency"
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Low - Can wait a few days</option>
                    <option value="medium">Medium - Need help within 24-48 hours</option>
                    <option value="high">High - Urgent, need help today</option>
                    <option value="emergency">Emergency - Critical deadline</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Detailed Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={6}
                  value={formData.description}
                  onChange={handleChange}
                  required
                  placeholder="Please describe your assignment, project, or problem in detail. Include any specific requirements, error messages, or constraints."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label htmlFor="attachments" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Attach Files (Optional)
                </label>
                <input
                  type="file"
                  id="attachments"
                  name="attachments"
                  onChange={handleFileChange}
                  accept=".py,.js,.jsx,.html,.css,.txt,.md"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Accepted formats: .py, .js, .jsx, .html, .css, .txt, .md
                </p>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting Request...' : 'Submit Support Request'}
              </button>
              
              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center text-green-600 dark:text-green-400"
                >
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Support request submitted successfully! I'll get back to you soon.
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </section>

      {/* Guidelines */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Support Guidelines
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Important information about our support process
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    What I Can Help With
                  </h3>
                </div>
                <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                  <li>• Code debugging and error resolution</li>
                  <li>• Algorithm explanation and optimization</li>
                  <li>• Project architecture and best practices</li>
                  <li>• Learning new technologies and frameworks</li>
                  <li>• Portfolio project guidance</li>
                  <li>• Career advice and interview preparation</li>
                </ul>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <InformationCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Response Times
                  </h3>
                </div>
                <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                  <li>• Emergency: 2-4 hours</li>
                  <li>• High priority: 8-12 hours</li>
                  <li>• Medium priority: 24-48 hours</li>
                  <li>• Low priority: 3-5 days</li>
                </ul>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    What I Cannot Do
                  </h3>
                </div>
                <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                  <li>• Complete assignments for you</li>
                  <li>• Write entire projects from scratch</li>
                  <li>• Help with academic dishonesty</li>
                  <li>• Provide solutions without explanation</li>
                  <li>• Work on proprietary or confidential code</li>
                  <li>• Guarantee specific grades or outcomes</li>
                </ul>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <AcademicCapIcon className="h-6 w-6 text-purple-600 dark:text-purple-400 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Teaching Approach
                  </h3>
                </div>
                <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                  <li>• Focus on understanding concepts</li>
                  <li>• Guide through problem-solving process</li>
                  <li>• Provide explanations and examples</li>
                  <li>• Encourage independent learning</li>
                  <li>• Build confidence and skills</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Support;
